import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { AuthClient } from './auth-client'
import AuthContext from './auth-context'
import { hasAuthParams } from './auth-utils'
import { reducer } from './auth-reducer'
import { contextOriginService } from './contextOriginService'
import { initialAuthState } from './auth-state'
import { setData, resetStore } from '@common/utils/resource'
import analytics from '@helpers/segment'
import Loader from '@app/common/layout/components/Loader'
import { defaultAnonymousUser, getLDUser } from '@helpers/ldHelpers'
import { setLoanIdForEncompass } from '@app/common/socket/actions'
import { getRedirectLink } from '@app/common/utils/helpers'
import { isAuthError, createLoginFailedError } from './auth-errors'
import authLogger from './auth-logger'

const defaultOnRedirectCallback = () => {
  window.history.replaceState({}, document.title, window.location.pathname)
}

const defaultSkipAuthCheckCallback = (url = window.location.href) => {
  // Skip auth flow unless the path is /auth/login
  // This allows user activation and reset password to function without triggering auth flow
  try {
    const urlObj = new URL(url)
    return urlObj.pathname !== '/auth/login'
  } catch (error) {
    // If URL parsing fails, default to skipping auth
    authLogger.warn('Error parsing skip auth check', error)
    return true
  }
}

const AuthProvider = (opts) => {
  const {
    children,
    skipAuthCheckCallback = defaultSkipAuthCheckCallback,
    onRedirectCallback = defaultOnRedirectCallback,
    authConfig,
  } = opts
  const storeDispatch = useDispatch()
  const history = useHistory()
  const ldClient = useLDClient()
  const didInitialise = useRef(false)
  const ldTimeoutRef = useRef(null)
  const [state, dispatch] = useReducer(reducer, { ...initialAuthState, ...authConfig })
  const [client] = useState(() => new AuthClient(authConfig.use_identity_login, authConfig.use_frame))

  /**
   * Handle session start (client side)
   */
  const sessionStartHandler = useCallback(async () => {
    await client.clearUser()
    const user = await client.getUser()

    storeDispatch(setData(user, { resource: { namespace: 'user' } }))

    analytics.userLogIn(user)
    if (ldClient) {
      ldClient.identify(getLDUser(user))
    }

    dispatch({ type: 'LOGIN', user })
  }, [storeDispatch, dispatch, client, ldClient])

  /**
   * Handle session end (client side)
   */
  const sessionEndHandler = useCallback(async () => {
    await client.clearUser()
    // TODO: PLAT-2904 Re-evaluate clearing the entire redux store, it only clears the `resource` namespace not all data
    storeDispatch(resetStore())

    analytics.userLogOut()
    if (ldClient) {
      ldClient.identify(defaultAnonymousUser)
    }

    dispatch({ type: 'LOGOUT' })
  }, [storeDispatch, dispatch, client, ldClient])

  /**
   * Consistently clear errors when authentication actions succeed
   */
  const onAuthenticationSuccess = useCallback(async () => {
    dispatch({ type: 'CLEAR_ERROR' })
  }, [dispatch])

  /**
   * Clear any authentication errors (e.g., when navigating away from login)
   */
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' })
  }, [dispatch])

  /**
   * Consistently set errors when authentication actions fail
   */
  const onAuthenticationError = useCallback((error, notify = true) => {
    if (notify) {
      if (isAuthError(error)) {
        dispatch({ type: 'SET_ERROR', error: error.message })
      } else {
        dispatch({ type: 'SET_ERROR', error: 'Login failed. Please try logging in again.' })
      }
    }
  }, [dispatch])

  /**
   * This will redirect the user to the given URL or the default redirect URL based on logged in permissions
   */
  const redirectUser = useCallback((redirectUrl) => {
    const suppliedRedirectUrl = redirectUrl
    const permissionRedirectUrl = getRedirectLink()
    const finalRedirectUrl = suppliedRedirectUrl ?? permissionRedirectUrl

    authLogger.debug(`auth: redirecting to ${finalRedirectUrl}`)
    history.push(finalRedirectUrl)
  }, [history])

  /**
   * This will log the user in and redirect them to the appropriate page per their permissions
   */
  const login = useCallback(
    async (opts) => {
      try {
        await client.login(opts)
        await onAuthenticationSuccess()
        await sessionStartHandler()
        redirectUser()
      } catch (error) {
        onAuthenticationError(error)

        if (isAuthError(error)) {
          throw error
        }

        // Always throw login failed error for any other errors
        throw createLoginFailedError()
      }
    },
    [client, onAuthenticationSuccess, onAuthenticationError, sessionStartHandler, redirectUser],
  )

  /**
   * This will log the user out and redirect them to the logout page
   */
  const logout = useCallback(async () => {
    // Redirect to logout route to handle logout process
    history.replace('/auth/logout')
  }, [history])

  /**
   * Log the user out and end the session
   */
  const endSession = useCallback(async () => {
    try {
      await client.logout()
    } catch (error) {
      onAuthenticationError(error, false)
    } finally {
      await sessionEndHandler()
    }
  }, [client, onAuthenticationError, sessionEndHandler])

  /**
   * Handle redirect callback handles token based logins and auth code logins
   */
  const handleRedirectCallback = useCallback(async () => {
    try {
      const redirectResult = await client.loginRedirectCallback()

      if (redirectResult.reggoraOrigin) {
        contextOriginService.setToken(redirectResult.reggoraOrigin)
      }
      if (redirectResult.encompassLoanId) {
        storeDispatch(setLoanIdForEncompass(redirectResult.encompassLoanId))
      }

      await onAuthenticationSuccess()
      await sessionStartHandler()
      redirectUser(redirectResult.redirectUrl)
    } catch (error) {
      onAuthenticationError(error)
      await sessionEndHandler()
    } finally {
      onRedirectCallback()
    }
  }, [
    client,
    onAuthenticationSuccess,
    onAuthenticationError,
    sessionStartHandler,
    sessionEndHandler,
    redirectUser,
    storeDispatch,
    onRedirectCallback,
  ])

  /**
   * Handle existing user login session (silent auth)
   */
  const handleExistingSession = useCallback(async () => {
    try {
      await client.checkSession()
      await onAuthenticationSuccess()
      await sessionStartHandler()
    } catch (error) {
      // Existing session errors will log the user out silently
      onAuthenticationError(error, false)
      await sessionEndHandler()
    }
  }, [client, onAuthenticationSuccess, onAuthenticationError, sessionStartHandler, sessionEndHandler])

  const initializeAuth = useCallback(async () => {
    if (didInitialise.current) {
      return
    }
    didInitialise.current = true

    // Clear any existing timeout
    if (ldTimeoutRef.current) {
      clearTimeout(ldTimeoutRef.current)
      ldTimeoutRef.current = null
    }

    authLogger.debug('Initialising provider')

    try {
      if (hasAuthParams() && !skipAuthCheckCallback()) {
        authLogger.debug('logging in externally')
        // Here we'll process the login from query params
        // We'll support processing JWTs (legacy support)
        // And we'll support auth code (new flow)
        await handleRedirectCallback()
      } else {
        authLogger.debug('checking existing session')
        // Here we'll process the current user session
        // Which will start with a local storage JWT (for legacy support)
        // Will perform silent authentication to hydrate a fresh JWT off the session (new flow)
        await handleExistingSession()
      }
    } catch (error) {
      // Any error that reaches here considers the user as not authenticated
      dispatch({ type: 'NO_USER' })
    }
  }, [handleExistingSession, handleRedirectCallback, skipAuthCheckCallback])

  const contextValue = {
    ...state,
    login,
    logout,
    endSession,
    clearError,
  }

  useEffect(() => {
    if (didInitialise.current) {
      return
    }

    // Set up timeout to proceed without LaunchDarkly after .75 seconds
    ldTimeoutRef.current = setTimeout(() => {
      authLogger.error('LaunchDarkly timeout, proceeding without it')
      initializeAuth()
    }, 750)

    // If ldClient is available, proceed immediately
    if (ldClient) {
      clearTimeout(ldTimeoutRef.current)
      ldTimeoutRef.current = null
      initializeAuth()
    }

    // Cleanup timeout on unmount
    return () => {
      if (ldTimeoutRef.current) {
        clearTimeout(ldTimeoutRef.current)
        ldTimeoutRef.current = null
      }
    }
  }, [
    ldClient,
    initializeAuth,
  ])

  // Don't render children until auth is initialized
  if (!state.isInitialized) {
    return (
      <Loader />
    )
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export default AuthProvider
